import { PublishModal } from "@/Pages/Studio/AdBuilder/FormActionButtons";
import { mutateAdDelete, mutateAdIndex, mutateAdPause, mutateAdResume } from "@/Services";
import { RowActionsMenu } from "@/Components/ui";
import { AD_STATUS, AD_STATUS_EXTENSION_CODE, DESIGN_LOCKED_STATUSES, DESIGN_STATUS } from "@/Constants/adStatus";
import { useDuplicateAdModal } from "./DuplicateAdModal";
import { useAdTagsModal } from "./AdTagsModal";
import { Link } from "@inertiajs/react";
import { ActionIcon, Menu, Text, rem } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconArchive, IconCalendarEvent, IconCircleCheck, IconCode, IconCopy, IconMessage, IconPlayerPause, IconPlayerPlay, IconScanEye, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import classes from "@/Pages/Studio/AdBuilder/ad-list.module.css";
import { useAdStudioRole } from "@/Hooks/useAdStudioRole";

// Per-status action layout decoded from the row-actions dropdown design:
// each status shows one primary icon outside the "..." menu, plus a
// status-specific set of menu items.
const STATUS_ACTIONS = {
    draft: { primary: 'edit', menu: ['preview', 'duplicate', 'divider', 'delete'] },
    paused: { primary: 'preview', menu: ['edit', 'resume', 'share', 'adTags', 'divider', 'delete'] },
    expired: { primary: 'analytics', menu: ['edit', 'preview', 'duplicate', 'adTags', 'divider', 'delete'] },
    published: { primary: 'analytics', menu: ['edit', 'editDelivery', 'preview', 'pause', 'duplicate', 'adTags', 'divider', 'delete'] },
    archived: { primary: 'preview', menu: ['edit', 'duplicate'] },
    // AD-220: "Let Us Design It". A Draft briefing is continued on the briefing page;
    // while the designers have the ad, it can only be looked at.
    briefingDraft: { primary: 'briefing', menu: ['delete'] },
    inDesign: { primary: 'briefing', menu: [] },
    // Sent for approval (AD-225): the email's actions, plus the briefing to look at.
    inApproval: { primary: 'approveDesign', menu: ['approveDesign', 'requestChanges', 'preview', 'divider', 'briefing'] },
    // Approved, waiting for delivery and targeting (AD-225): "Set Up Delivery".
    setupNeeded: { primary: 'setupDelivery', menu: ['setupDelivery', 'preview'] },
};

/**
 * AD-225: the approved design of a "Let Us Design It" ad that still needs delivery
 * and targeting, if any.
 */
export const setupNeededOf = (ad) => (!ad?.is_archived && ad?.design_request?.needs_delivery_setup ? ad.design_request : null);


/**
 * AD-220: the briefing of a "Let Us Design It" ad that is not booked yet, if any.
 */
export const briefingOf = (ad) => {
    // An archived ad is handled as any archived ad.
    if (ad?.is_archived) return null;
    const status = ad?.design_request?.status;
    return status === 'draft' || DESIGN_LOCKED_STATUSES.includes(status) ? ad.design_request : null;
};

const getStatusKey = (row) => {
    if (row.original.is_archived) return 'archived';
    if (briefingOf(row.original)) {
        const { status, review } = row.original.design_request;
        if (status === 'draft') return 'briefingDraft';
        // The links come only for an account that may answer the design.
        return status === DESIGN_STATUS.IN_APPROVAL.code && review ? 'inApproval' : 'inDesign';
    }
    if (setupNeededOf(row.original)) return 'setupNeeded';
    if (row.original.status == AD_STATUS.PAUSED.code) return 'paused';
    if (row.original.status == AD_STATUS.PUBLISHED.code) return 'published';
    if (row.original.status == AD_STATUS_EXTENSION_CODE) return 'published';
    if (row.original.status == AD_STATUS.DONE.code) return 'published';
    if (row.original.status == AD_STATUS.EXPIRED.code) return 'expired';
    return 'draft';
};

export const TableActions = ({ table, row }) => {

    const { t } = useTranslation();
    // AD-222: an Editor opens the design in the editor; booking, copying and deleting
    // are the customer's (the API refuses them too).
    const { isEditor } = useAdStudioRole();

    const mutate = mutateAdIndex({ id: row.original.id });
    const mutateDelete = mutateAdDelete({ id: row.original.id });
    const editRoute = Number(row.original.ad_origin) === 9
        ? route('studio.studio_editor_v2.edit', { adId: row.original.id })
        : route('studio.ad_builder.edit', { ad: row.original.id });
    const mutatePause = mutateAdPause({ id: row.original.id });
    const mutateResume = mutateAdResume({ id: row.original.id });

    const { open: openDuplicate, modal: duplicateModal } = useDuplicateAdModal(row.original);
    const { open: openAdTags, modal: adTagsModal } = useAdTagsModal(row.original);

    const openTagPublisher = () => mutate.mutate({}, {
        onSuccess: ({ data }) => {

            const rowAd = data[0] || {};

            PublishModal({ query: { data: rowAd }, withFooter: false });
        }
    });

    const deleteAction = () => modals.openConfirmModal({
        title: t('ads.deleteTitle'),
        children: (
            <Text size="sm">
                {t('ads.deleteConfirm')} <Text span fw="bold">"{row.original.name}"</Text>?
            </Text>
        ),
        labels: { confirm: t('common.confirm'), cancel: t('common.cancel') },
        confirmProps: { leftSection: <IconTrash size={20} stroke={1.5} /> },
        onCancel: () => console.log('Cancel'),
        onConfirm: () => mutateDelete.mutate({}, {
            onSuccess: ({ data }) => {
                table.options.meta.refetch();
            }
        }),
    });

    // AD-207: status_pending is a client-side flag, not an API field. It rides along
    // on the cached row through the same updateRow the status change already uses, which
    // is what lets the status cell show the spinner while this menu holds the mutation.
    const patchRow = (patch) => table.options.meta.updateRow(row.original.id, patch);
    const isStatusPending = Boolean(row.original.status_pending);

    const changeStatus = (mutation, code) => {
        patchRow({ status_pending: true });

        mutation.mutate(undefined, {
            // The request is the confirmation: it only returns once DV360 has accepted
            // the change, so the spinner stops exactly when the new status is real.
            onSuccess: () => patchRow({ status: code, status_pending: false }),
            onError: () => patchRow({ status_pending: false }),
        });
    };

    const pauseAction = () => changeStatus(mutatePause, AD_STATUS.PAUSED.code);

    const resumeAction = () => changeStatus(mutateResume, AD_STATUS.PUBLISHED.code);

    const { primary, menu: statusMenu } = isEditor ? { primary: 'edit', menu: [] } : STATUS_ACTIONS[getStatusKey(row)];
    // Pause and Resume only when the backend allows them (Ad::canBePaused /
    // canBeResumed: not after the end date, not on a deleted ad).
    // Duplicate only where the backend takes it (can_duplicate): not a "Let Us Design It" ad's
    // creative (CreativeLock).
    const menu = statusMenu.filter((item) => (item !== 'pause' || row.original.can_pause)
        && (item !== 'resume' || row.original.can_resume)
        && (item !== 'duplicate' || row.original.can_duplicate));

    const PRIMARY_ICON_PROPS = {
        preview: {
            component: 'a', target: '_blank', href: row.original.ad_preview,
            variant: 'default', 'aria-label': t('common.preview'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6.6665 8.00002C6.6665 8.35364 6.80698 8.69278 7.05703 8.94283C7.30708 9.19288 7.64622 9.33335 7.99984 9.33335C8.35346 9.33335 8.6926 9.19288 8.94265 8.94283C9.19269 8.69278 9.33317 8.35364 9.33317 8.00002C9.33317 7.6464 9.19269 7.30726 8.94265 7.05721C8.6926 6.80716 8.35346 6.66669 7.99984 6.66669C7.64622 6.66669 7.30708 6.80716 7.05703 7.05721C6.80698 7.30726 6.6665 7.6464 6.6665 8.00002Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M14 8C12.4 10.6667 10.4 12 8 12C5.6 12 3.6 10.6667 2 8C3.6 5.33333 5.6 4 8 4C10.4 4 12.4 5.33333 14 8Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )
        },
        analytics: {
            component: 'a', target: '_blank', href: row.original.ad_dashboard, color: 'green', variant: 'default', 'aria-label': t('ads.analytics'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 2V14H14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M13.3335 12V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10.6665 10.6667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M8 8.66667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M5.3335 10.6667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M2 7.33333C6 7.33333 5.33333 4 8 4C10.6667 4 10 7.33333 14 7.33333" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )
        },
        edit: {
            component: Link,
            href: editRoute,
            color: 'dark', variant: 'default',
            size: 'md', 'aria-label': t('common.edit'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.6665 4.66666H3.99984C3.64622 4.66666 3.30708 4.80713 3.05703 5.05718C2.80698 5.30723 2.6665 5.64637 2.6665 5.99999V12C2.6665 12.3536 2.80698 12.6928 3.05703 12.9428C3.30708 13.1928 3.64622 13.3333 3.99984 13.3333H9.99984C10.3535 13.3333 10.6926 13.1928 10.9426 12.9428C11.1927 12.6928 11.3332 12.3536 11.3332 12V11.3333" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M14.0001 3.40001C14.0001 3.77133 13.8526 4.12745 13.59 4.39001L8 10H6V8.00001L11.61 2.41001C11.8726 2.14745 12.2287 1.99994 12.6 1.99994C12.9713 1.99994 13.3274 2.14745 13.59 2.41001C13.8526 2.67257 14.0001 3.02869 14.0001 3.40001Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10.6665 3.33334L12.6665 5.33334" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )
        },
    };

    const briefing = briefingOf(row.original);
    if (briefing) {
        const href = route('studio.briefing.edit', { designRequest: briefing.id });
        // A Draft is continued (pencil); a submitted briefing is only looked at (eye).
        PRIMARY_ICON_PROPS.briefing = briefing.status === 'draft'
            ? { ...PRIMARY_ICON_PROPS.edit, href }
            : { ...PRIMARY_ICON_PROPS.preview, component: Link, target: undefined, href, 'aria-label': t('ads.viewBriefing') };
    }

    // AD-225: the "Approve Design" and "Request Changes" links of the email. Approving
    // asks first, as on the Design Feedback page, then continues to the Delivery step.
    const review = row.original.design_request?.review;
    const approveAction = () => modals.openConfirmModal({
        title: t('design.feedback.approveConfirm.title'),
        children: <Text size="sm">{t('design.feedback.approveConfirm.text')}</Text>,
        labels: { confirm: t('design.feedback.approve'), cancel: t('common.cancel') },
        confirmProps: { color: 'green', leftSection: <IconCircleCheck size={20} stroke={1.5} /> },
        onConfirm: () => { window.location.href = review.approve_url; },
    });
    PRIMARY_ICON_PROPS.approveDesign = {
        onClick: approveAction, color: 'green', variant: 'default', size: 'md',
        'aria-label': t('design.feedback.approve'), title: t('design.feedback.approve'),
        icon: <IconCircleCheck style={{ width: rem(16), height: rem(16) }} stroke={1.5} />,
    };

    const deliveryHref = route('studio.ad_builder.edit', { ad: row.original.id, step: 'delivery' });
    PRIMARY_ICON_PROPS.setupDelivery = {
        component: Link, href: deliveryHref, color: 'dark', variant: 'default', size: 'md',
        'aria-label': t('ads.setUpDelivery'), title: t('ads.setUpDelivery'),
        icon: <IconCalendarEvent style={{ width: rem(16), height: rem(16) }} stroke={1.5} />,
    };

    const renderPrimary = () => {
        const { icon, ...props } = PRIMARY_ICON_PROPS[primary];
        return <ActionIcon {...props}>{icon}</ActionIcon>;
    };

    const MENU_ITEMS = {
        preview: (
            <Menu.Item key="preview" leftSection={(
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6.6665 8.00002C6.6665 8.35364 6.80698 8.69278 7.05703 8.94283C7.30708 9.19288 7.64622 9.33335 7.99984 9.33335C8.35346 9.33335 8.6926 9.19288 8.94265 8.94283C9.19269 8.69278 9.33317 8.35364 9.33317 8.00002C9.33317 7.6464 9.19269 7.30726 8.94265 7.05721C8.6926 6.80716 8.35346 6.66669 7.99984 6.66669C7.64622 6.66669 7.30708 6.80716 7.05703 7.05721C6.80698 7.30726 6.6665 7.6464 6.6665 8.00002Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M14 8C12.4 10.6667 10.4 12 8 12C5.6 12 3.6 10.6667 2 8C3.6 5.33333 5.6 4 8 4C10.4 4 12.4 5.33333 14 8Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )} component="a" href={row.original.ad_preview} target="_blank">
                {t('ads.previewAd')}
            </Menu.Item>
        ),
        edit: (
            <Menu.Item key="edit" component={Link} href={editRoute} leftSection={(
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.6665 4.66666H3.99984C3.64622 4.66666 3.30708 4.80713 3.05703 5.05718C2.80698 5.30723 2.6665 5.64637 2.6665 5.99999V12C2.6665 12.3536 2.80698 12.6928 3.05703 12.9428C3.30708 13.1928 3.64622 13.3333 3.99984 13.3333H9.99984C10.3535 13.3333 10.6926 13.1928 10.9426 12.9428C11.1927 12.6928 11.3332 12.3536 11.3332 12V11.3333" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M14.0001 3.40001C14.0001 3.77133 13.8526 4.12745 13.59 4.39001L8 10H6V8.00001L11.61 2.41001C11.8726 2.14745 12.2287 1.99994 12.6 1.99994C12.9713 1.99994 13.3274 2.14745 13.59 2.41001C13.8526 2.67257 14.0001 3.02869 14.0001 3.40001Z" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10.6665 3.33334L12.6665 5.33334" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )}>
                {t('ads.editPageTitle')}
            </Menu.Item>
        ),
        editDelivery: (
            <Menu.Item key="edit-delivery" component={Link} href={route('studio.ad_builder.edit', { ad: row.original.id, step: 'delivery' })} leftSection={(
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.33337 11.6667H4.66671L10.7917 5.54167C10.9449 5.38846 11.0664 5.20657 11.1494 5.00639C11.2323 4.80622 11.275 4.59167 11.275 4.375C11.275 4.15833 11.2323 3.94378 11.1494 3.7436C11.0664 3.54343 10.9449 3.36154 10.7917 3.20833C10.6385 3.05512 10.4566 2.93359 10.2564 2.85067C10.0563 2.76776 9.84171 2.72508 9.62504 2.72508C9.40837 2.72508 9.19382 2.76776 8.99365 2.85067C8.79347 2.93359 8.61158 3.05512 8.45837 3.20833L2.33337 9.33333V11.6667Z" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M7.875 3.79167L10.2083 6.12501" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.91724 11.0833C9.91724 11.3928 10.0402 11.6895 10.2589 11.9083C10.4777 12.1271 10.7745 12.25 11.0839 12.25C11.3933 12.25 11.6901 12.1271 11.9089 11.9083C12.1277 11.6895 12.2506 11.3928 12.2506 11.0833C12.2506 10.7739 12.1277 10.4772 11.9089 10.2584C11.6901 10.0396 11.3933 9.91667 11.0839 9.91667C10.7745 9.91667 10.4777 10.0396 10.2589 10.2584C10.0402 10.4772 9.91724 10.7739 9.91724 11.0833Z" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M11.0839 9.04167V9.91667" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M11.0839 12.25V13.125" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.852 10.0625L12.0942 10.5" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10.0741 11.6667L9.3158 12.1042" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.3158 10.0625L10.0741 10.5" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.0942 11.6667L12.8526 12.1042" stroke="#212529" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            )}>
                {t('ads.editDelivery')}
            </Menu.Item>
        ),
        share: (
            <Menu.Item key="share" leftSection={
                (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 2V14H14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M13.3335 12V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M10.6665 10.6667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M8 8.66667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M5.3335 10.6667V14" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M2 7.33333C6 7.33333 5.33333 4 8 4C10.6667 4 10 7.33333 14 7.33333" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                )} onClick={openTagPublisher}>
                {t('booking.tag.getAdTags')}
            </Menu.Item>
        ),
        duplicate: (
            <Menu.Item key="duplicate" leftSection={<IconCopy style={{ width: rem(14), height: rem(14) }} />} onClick={openDuplicate}>
                {t('ads.duplicateAd')}
            </Menu.Item>
        ),
        pause: (
            <Menu.Item key="pause" disabled={isStatusPending} leftSection={<IconPlayerPause style={{ width: rem(14), height: rem(14) }} />} onClick={pauseAction}>
                {t('ads.pauseDelivery')}
            </Menu.Item>
        ),
        resume: (
            <Menu.Item key="resume" disabled={isStatusPending} leftSection={<IconPlayerPlay style={{ width: rem(14), height: rem(14) }} />} onClick={resumeAction}>
                {t('ads.resume')}
            </Menu.Item>
        ),
        adTags: (
            <Menu.Item key="ad-tags" leftSection={<IconCode style={{ width: rem(14), height: rem(14) }} />} onClick={(event) => {
                // The dropdown is portaled, so the row's click guard cannot see a <td>
                // and would open the preview modal on top of this one.
                event.stopPropagation();
                openAdTags();
            }}>
                {t('ads.customAdTags')}
            </Menu.Item>
        ),
        // No backend endpoint exists yet for archiving an ad (see AdStudioAdApiController /
        // AdService — only pause/resume are implemented). Left disabled on purpose.
        archive: (
            <Menu.Item key="archive" disabled leftSection={<IconArchive style={{ width: rem(14), height: rem(14) }} />}>
                {t('ads.archive')}
            </Menu.Item>
        ),
        delete: (
            <Menu.Item key="delete" color="red" leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />} onClick={deleteAction}>
                {t('ads.moveToArchive')}
            </Menu.Item>
        ),
        setupDelivery: (
            <Menu.Item key="setup-delivery" component={Link} href={deliveryHref} leftSection={<IconCalendarEvent style={{ width: rem(14), height: rem(14) }} />}>
                {t('ads.setUpDelivery')}
            </Menu.Item>
        ),
        approveDesign: (
            <Menu.Item key="approve-design" onClick={approveAction} leftSection={<IconCircleCheck style={{ width: rem(14), height: rem(14) }} />}>
                {t('design.feedback.approve')}
            </Menu.Item>
        ),
        requestChanges: (
            <Menu.Item key="request-changes" component="a" href={review?.feedback_url} leftSection={<IconMessage style={{ width: rem(14), height: rem(14) }} />}>
                {t('design.feedback.submit')}
            </Menu.Item>
        ),
        briefing: (
            <Menu.Item key="briefing" component={Link} href={briefing ? route('studio.briefing.edit', { designRequest: briefing.id }) : undefined} leftSection={<IconScanEye style={{ width: rem(14), height: rem(14) }} />}>
                {t('ads.viewBriefing')}
            </Menu.Item>
        ),
        divider: (
            <Menu.Divider key="divider" />
        )
    };

    return (
        <>
            <RowActionsMenu
                className={classes.rowActions}
                dropdownClassName={classes.actionsDropdown}
                primary={renderPrimary()}
                items={menu.map((key) => MENU_ITEMS[key])}
            />

            {duplicateModal}
            {adTagsModal}
        </>
    )
}
